/*----------------------------------------------------------------------------------------

Hello, welcome to introduction to how the F do I use JsCarousel?

Eg.

let wrapper = $('#whatever');
let options = {
    endpoint: 'carousels/homepage/featuredshows/offset/{offset}/limit/{limit}/'
}
wrapper.JsCarousel(options);

** by default `offset` and `limit` will be set with default values

----------------------------------------------------------------------------------------
            __  _
 ___  ___  / /_(_)__  ___  ___
/ _ \/ _ \/ __/ / _ \/ _ \(_-<
\___/ .__/\__/_/\___/_//_/___/
   /_/
----------------------------------------------------------------------------------------

let options = {
    endPoint: '/WHERE/MY/API/ENDPOINT/AT/BOI/{placeholder1}/AND/{placeholder2}/',

    // PARAMS THAT WILL REPLACE IN ENDPOINT (optional) (offset and limit have default values, you don't need to provide)
    params: {
        placeholder1: 52,
        placeholder2: 4
    },

    // PASS ANY TYPE OF DROPDOWN FILTER INSTANCE HERE (optional)
    filters: new DropdownFilter([
            {
                title: 'Option A',
                value: 1
            }
            {
                title: 'Option B',
                value: 2
            }
        ]),


    // SLICK.JS SETTINGS FOR `responsive` (optional)
    responsive: [
        {
            breakpoint: 1024,
            settings: {
                slidesToShow: 4,
                slidesToScroll: 4
            }
        }
    ]

};

----------------------------------------------------------------------------------------
                    __
 ___ _  _____ ___  / /____
/ -_) |/ / -_) _ \/ __(_-<
\__/|___/\__/_//_/\__/___/

----------------------------------------------------------------------------------------

wrapper.on('videoDataLoad', (e, result) => {
    console.log('data pulled');
});

- `videoDataLoad`
Arguments: result
Fires after data is pulled successfully

- `videoDataRendered`
Arguments: result
Fires after data is pulled successfully and new items are rendered

- `videoDataLoadError`
Fires after data failed to load from endpoint

- `templateDidRender`
Fires after the DOM is rendered

- ALL EVENTS THAT SLICK.JS FIRES
Check out: http://kenwheeler.github.io/slick/

----------------------------------------------------------------------------------------*/

import layout from 'templates/carousels/layout.tpl';
import template from 'templates/carousels/__carousel.tpl';
import 'carousels/helpers/ellipsify';
import { Navigation, Virtual, Autoplay } from 'swiper/modules';
import { Swiper } from 'swiper/bundle';
Swiper.use([Navigation, Virtual, Autoplay]);
import { getLocale } from 'services/LocalizationService';
import { trackAction } from 'helpers/trackingOM';
import FathomTracking, { Tracking } from 'shared/fathomTracking';
import ImpressionTracking, {
    CarouselTracking,
    hasImpressionTracking,
} from 'shared/impressionTracking';
import { UpdateFocusableCarousel } from 'components/accessibilityCarousel';
import { JsCarouselTracking } from '../../carousels/helpers/JsCarouselTracking';
import Domain from 'helpers/Domain'; // ~/src/web-package-coreui/src/js/helpers/Domain.js
import DMAStorageService from 'services/DMAStorageService';
import scrollCarouselManager from './ScrollCarouselManager.js';

// START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel
import { allowContinuousScroll } from '../../abTests/testClasses/HomePageContinuousScroll';
// END AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel

// START A/B TEST PLGRWEB-1649-add to my list button from my list page
import { forceVirtualSlides } from '../../abTests/testClasses/MyListPageAddToMyList';
// END A/B TEST PLGRWEB-1649-add to my list button from my list page

import { CustomMeasure } from '@cbsi-cbscom/custom-measurement';

const JS_CAROUSEL = {
    NAMESPACE: 'JS_CAROUSEL',
    LOADED: 'LOADED',
};

const customMeasure = new CustomMeasure({ namespace: JS_CAROUSEL.NAMESPACE });

const ZOOM_CAROUSEL_EFFECT_DISABLED_CLASS = 'zoom-carousel-effect-disabled';

import { CarouselProfileDropdown } from './CarouselProfileDropdown';

import { initializedAddToMyListFromYAMLInstance } from '../watchlist/AddToMyListFromYAML';

export class JsCarousel {
    static CarouselVideoPlayEvent = new Event('carouselVideoStart');
    static CarouselVideoStopEvent = new Event('carouselVideoStop');

    disableOnScroll = false;

    constructor(element, options) {
        let defaultOptions = {
            infinite: false,
            layout: layout,
            template: template,
            useBuffer: true,
            prevArrow:
                '<a class="carousel-prev focusable" tabindex="0" role="button" aria-label="Previous Slides"></a>',
            nextArrow:
                '<a class="carousel-next focusable" tabindex="0" role="button" aria-label="Next Slides"></a>',

            // stores the xhr response, used for arrow tracking
            xhrResult: '',
        };

        this.options = $.extend(defaultOptions, options);

        this._ = element;
        this._wrapper = $(this._);
        this.showId = null;
        this.virtualSlides = null;
        this.lastIndex = 0;
        this.lastBreakPoint = null;
        this.maxSlides = 6;
        this.guideItem;
        this.position = null;
        this.lastSlidesPerGroup = null;
        this.bodyTag = $('body');
        this.hasFathomTracking = hasImpressionTracking(true);
        this.prevWidth = $(window).width(); // prevent resize event from firing on load
        this.resizing = false;
        this.excludedContentIds = JSON.parse(localStorage.getItem('excludedContentIds')) || [];
        this.deletedStoredTimer = null;
        this.addedNewContentId = false;

        /*
        the breakpoint settings for the new Swiper Lib has change
            for example:     
                640: {
                         slidesPerView: 2,
                },
        the old Swiper takes 640 as end break point, means anything less than 640 will have slidesPerView 2
        in new Swiper,takes 640 as beginning point, mean anything bigger than 640 will have slidesPerView 2
         */
        if (!this.options.responsive.hasOwnProperty(0)) {
            let lastBreakPoint = null;
            let responsiveOption = {};
            for (const [breakpoint] of Object.entries(this.options.responsive)) {
                !lastBreakPoint
                    ? (responsiveOption[0] = this.options.responsive[breakpoint])
                    : (responsiveOption[lastBreakPoint] = this.options.responsive[breakpoint]);
                lastBreakPoint = breakpoint;
            }
            this.options.responsive = responsiveOption;
        }

        this.__initScrollService();
        this.__init();
        this.__trackCarousels();

        // START A/B TEST PLGRWEB-1649-add to my list button from my list page
        if (
            CBS?.AbTestsConfigs?.modules?.MyListPageAddToMyList?.inVariants &&
            this._wrapper.attr('id') === 'rfy-hybrid-carousel'
        ) {
            this._wrapper.trigger('videoDataLoad');
        }

        // trigger this to init tracking listeners for ymal (PHP carousel)
        if (this._wrapper.attr('id') === 'related-shows-carousel') {
            this._wrapper.trigger('videoDataRendered');
        }
        // END A/B TEST PLGRWEB-1649-add to my list button from my list page

        /* Creating a new instance of the DMAStorageService class. */
        this.DMAStorage = new DMAStorageService();

        this.timezone_offset_minutes = new Date().getTimezoneOffset();
        this.timezone_offset_minutes =
            this.timezone_offset_minutes === null || this.timezone_offset_minutes === undefined
                ? 0
                : this.timezone_offset_minutes;
        this.localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel
        this.continuousEndScroll = null;
        this.continuousEndScrollNextBuffer = false;
        // END AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel
        this.zoomCarouselEffectClass = this.getZoomCarouselEffectClass(options);
    }

    /**
     * Timer for stored deleted contentIds.
     */
    clearContentIdsStored() {
        clearInterval(this.deletedStoredTimer);
        localStorage.removeItem('excludedContentIds');
        this.excludedContentIds = [];
        this.addedNewContentId = false;
    }

    /**
     * Returns the CSS class for the zoom carousel effect based on the provided options.
     * If the options.zoomCarouselEffectDisabled is true, the CSS class to disable the
     * zoom carousel effect on the links is returned.
     * defaults to empty string for default zoom carousel effect.
     *
     * @param {Object} options - The options set on the js carousel.
     * @param {boolean} options.zoomCarouselEffectDisabled - Whether the zoom carousel effect is disabled.
     * @returns {string} The CSS class for the zoom carousel effect.
     */
    getZoomCarouselEffectClass(options = {}) {
        if (options?.zoomCarouselEffectDisabled) {
            return ZOOM_CAROUSEL_EFFECT_DISABLED_CLASS;
        }

        return '';
    }

    /**
     * This will be run if no endpoint is set (meaning static carousel)
     * @private
     */
    __setupSettingsForPhpCarousel() {
        this._swiperContainer = this._wrapper.find('.wrap');
        this.options.useBuffer = false;
    }

    allowEvents(allow = true) {
        let wrapper = this._wrapper[0];
        if (allow) {
            wrapper.style.pointerEvents = 'auto';
        } else {
            wrapper.style.pointerEvents = 'none';
        }
    }

    __initScrollService() {
        scrollCarouselManager.add(this);
    }

    __init() {
        this.__eventListeners();

        //If data available
        if (this.options.data) {
            this.__renderCarousel(this.options.data);
            this.__initFathomTracking();
            this.__initImpressionTracking();
            return;
        }

        let overridesParams = {};
        if (this.options.filter) {
            overridesParams.season = this.options.filter.options[0].season;
        }
        if (this.options.editPoint) {
            overridesParams.random = Math.floor(Math.random() * 100 + 1); // to force uncached version upon edit;
        }
        let endpoint = this.__buildEndpoint(overridesParams);
        this.lastBreakPoint = this.__checkBreakpoint();
        this.__pullAndRender(endpoint, (result) => {
            // save state of response (for arrow tracking)
            this.options.xhrResult = result;
            this.__initSwiper(result);
            this.total = (result && result.total) || 0;
            this._wrapper.closest('.js-le-carousel').show();
            this.__trackingArrow();
            this.__initFathomTracking();
            this.__initImpressionTracking();
            this.__initEdit();

            if (this?.options?.xhrResult?.data[0]?.addToMyListContentId) {
                initializedAddToMyListFromYAMLInstance()
                    .setCarouselTrackingData(this._wrapper.get(0))
                    .setClickActionsOnWatchCTAWrappers(this.swiper.visibleSlides);
            }
        });
    }

    __initFathomTracking() {
        if (this.hasFathomTracking) {
            JsCarousel.lastCarouselFlag = this._wrapper.data('displayid');
            this.fathomTracking = new FathomTracking(this._wrapper, this.swiper);
        }
    }

    __initImpressionTracking() {
        if (this.hasFathomTracking) {
            this.impressionTracking = new ImpressionTracking(this._wrapper, this.swiper);
        }
    }

    __deleteSelectedData(data, editPoint, reloadPoint, trackingParam) {
        const self = this;
        var s_params = {
            selections: data,
            editPoint: editPoint,
        };
        $.ajax({
            url: CBS.Registry.region.prefix + '/carousels/homepage/edit/',
            type: 'POST',
            dataType: 'json',
            data: s_params,
            error: function () {
                console.log('Error 1 - Ajax call failed.');
            },
            success: function (resp) {
                if (!resp.success) {
                    console.log('Error 2 - ' + resp.message);
                } else {
                    self.__trackingDeletedItems(resp.result, trackingParam);
                    //Use trackingParams to get the contentIds to be deleted and store them in the excludedContentIds array
                    $.each(data, function (index, value) {
                        var trackingParams = value.split('|');
                        self.excludedContentIds.push(trackingParams[9]);
                        self.addedNewContentId = true;
                    });

                    //store the excludedContentIds in localStorage
                    localStorage.setItem(
                        'excludedContentIds',
                        JSON.stringify(self.excludedContentIds),
                    );

                    self.__editAndReload(reloadPoint);
                }
            },
        });
    }

    __trackingDeletedItems(items, trackingParam) {
        $.each(items, function (index, value) {
            var trackingParams = value.split('|');
            var showSeriesId = trackingParams[0];
            var showSeriesTitle = trackingParams[1];
            var showGenre = trackingParams[2];
            var showDayPart = trackingParams[3];
            var showEpisodeId = trackingParams[4];
            var showEpisodeLabel = trackingParams[5];
            var showSeasonNumber = trackingParams[6];
            var showEpisodeNumber = trackingParams[7];
            var showAirDate = trackingParams[8];

            if (trackingParam == 'trackEditKW') {
                let objTracking;

                if (trackingParams[0] != '-1') {
                    objTracking = {
                        showSeriesId: showSeriesId,
                        showSeriesTitle: showSeriesTitle,
                        removeKWComplete: '1',
                    };
                } else {
                    objTracking = {
                        movieId: trackingParams[9],
                        movieTitle: trackingParams[5],
                        removeKWComplete: '1',
                    };
                }

                trackAction('trackRemoveKW', objTracking);
            }
        });
    }

    __setArrowPosition() {
        if (this.guideItem) {
            let $prevArrow = $(this.swiper.navigation.prevEl);
            let $nextArrow = $(this.swiper.navigation.nextEl);

            let isKidCarousel = this._wrapper.parents('.kids-carousel').length ? true : false;

            let height = $(this.swiper.slides[1]).find('.thumb-wrapper')[0].offsetHeight - 10; //margin-bottom
            let heightArrow = 10;
            let arrowPositionY = (height - heightArrow) * 0.5;

            if (isKidCarousel) {
                arrowPositionY = (height - heightArrow) * 0.75;
            }
            $nextArrow.css('background-position', `center ${arrowPositionY}px`);
            $prevArrow.css('background-position', `center ${arrowPositionY}px`);
        }
    }

    __trackingArrow() {
        if (this.swiper.navigation) {
            let $prevArrow = $(this.swiper.navigation.prevEl);
            let $nextArrow = $(this.swiper.navigation.nextEl);

            // set aa-link on jsCarousel arrows based on xhr response
            if (this.options.xhrResult) {
                $nextArrow.attr('aa-link', this.options.xhrResult.rightArrow);
                $prevArrow.attr('aa-link', this.options.xhrResult.leftArrow);
            }

            //adjust position

            if (this.swiper.slides[1]) {
                this.guideItem = $(this.swiper.slides[1]).find('.thumb-wrapper')[0];
                this.__setArrowPosition();
            }

            // setup arrow click handlers
            $nextArrow.on('click', function () {
                // execute tracking in timeout so it doesn't interrupt UI thread
                setTimeout(() => {
                    try {
                        om.trackClick({
                            item: $(this).attr('aa-link'),
                        });
                    } catch (e) {}
                }, 100);
            });

            $prevArrow.on('click', function () {
                // execute tracking in timeout so it doesn't interrupt UI thread
                setTimeout(() => {
                    try {
                        om.trackClick({
                            item: $(this).attr('aa-link'),
                        });
                    } catch (e) {}
                }, 100);
            });
        }
    }

    __initEdit() {
        if (this.options.editPoint) {
            const self = this;

            this._wrapper.find('.edit-cancel-carousel').on('click', function () {
                if (self.options.trackingParam === 'trackEditKW') {
                    trackAction('trackCancelKW', {
                        cancelKW: '1',
                    });
                }

                for (let i = 0; i < self.swiper.virtual.slides.length; i++) {
                    $('input[name=editor]:checked', self._wrapper).each(function (index) {
                        $(this).prop('checked', false);
                    });
                }

                $('input[name=editor]:checked', self._wrapper).each(function (index) {
                    $(this).prop('checked', false);
                });
                $(this).parent().find('a').attr('tabindex', '0'); // in case you clicked cancel while keyboard tab/focus
                $(this).parent().children('.video-carousel-box').addClass('editOff');
                $(this).parent().children('.video-carousel-box').removeClass('editOn');
                $(this).parent().children('.edit-carousel').text(getLocale('edit'));
                $(this).css('display', 'none');
            });

            this._wrapper.find('.edit-carousel').on('click', function () {
                if ($(this).text() == getLocale('remove')) {
                    self._wrapper.find('.edit-cancel-carousel').css('display', 'none');

                    var itemsSelected = [];

                    $('input[name=editor]:checked', self._wrapper).each(function (index) {
                        itemsSelected.push($(this).val());
                    });

                    $(this).parent().children('.video-carousel-box').removeClass('editOn');
                    $(this).parent().children('.video-carousel-box').addClass('editOff');

                    if (itemsSelected.length > 0) {
                        self.__deleteSelectedData(
                            itemsSelected,
                            self.options.editPoint,
                            self.options.reloadPoint,
                            self.options.trackingParam,
                        );
                        self._wrapper.find('.carousel-container').css('opacity', '0');
                    } else {
                        console.log('No items selected.');
                    }
                } else {
                    if (self.options.trackingParam === 'trackEditKW') {
                        trackAction('trackEditKW', {
                            editKW: '1',
                        });
                    }

                    $(this).parent().children('.video-carousel-box').addClass('editOn');
                    $(this).parent().children('.video-carousel-box').removeClass('editOff');
                    self._wrapper.find('.edit-cancel-carousel').css('display', 'block');
                }
                $(this).text(
                    $(this).text() == getLocale('remove') ? getLocale('edit') : getLocale('remove'),
                );
            });

            $('input[name=editor]').change(function () {
                if ($(this).is(':checked')) {
                    $(this).parent().children('.overlay').css('opacity', '.55');
                    $(this).parent().children('.overlay').css('background-color', '#1D1D1D');
                } else {
                    $(this).parent().children('.overlay').css('opacity', '1');
                    $(this)
                        .parent()
                        .children('.overlay')
                        .css(
                            'background',
                            'linear-gradient(180deg, rgba(0,0,0,0.5508578431372548) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 100%)',
                        );
                }
            });
        }
    }

    __initSwiper(result) {
        const self = this;

        let swiperParameters = {
            resizeObserver: false,
            watchSlidesProgress: true,
            simulateTouch: false,
            spaceBetween: 10,
            updateOnImagesReady: true,
            navigation: {
                nextEl: '.carousel-next',
                prevEl: '.carousel-prev',
            },
            on: {
                init: function () {
                    self._wrapper.trigger('init');
                },
                afterInit: self?.options?.on?.afterInit,
                beforeSlideChangeStart: self?.options?.on?.beforeSlideChangeStart,
                slideChange: self?.options?.on?.slideChange,
                resize: self?.options?.on?.resize,
            },
            autoplay: self?.options?.autoplay,
            breakpoints: self?.options?.responsive,
            watchSlidesVisibility: true,
        };

        // add pagination to spotlight if multiple slides
        if (result?.carouselPresentationStyle === 'spotlight' && result?.data?.length > 1) {
            swiperParameters.speed = 500;
            swiperParameters.navigation = false;
            swiperParameters.effect = 'fade';
            swiperParameters.fadeEffect = {
                crossFade: true,
            };
            swiperParameters.pagination = {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true,
                renderBullet: function (index, className) {
                    if (result?.data[index]?.bundleLocked) {
                        className += ' locked';
                    }

                    if (index === self.options.numPaginationToShow) {
                        className += ' disabled show';
                    } else if (index > self.options.numPaginationToShow) {
                        className += ' hide';
                    } else if (index === self.options.disabledPaginationIndex) {
                        className += ' fading';
                    } else {
                        className += ' show';
                    }

                    return `<span class="swiper-pagination-bullet ${className}" data-pos="${index}" style="order:${index + 1}"><i><img src="${result.data[index].thumbnail}" class="loaded" /><span class="locked-content"></span></i><b></b></span>`;
                },
            };

            // add multi/offset classes
            const $spotlightCarousel = this._wrapper.closest('.spotlight-carousel');
            $spotlightCarousel.addClass('spotlight-carousel-multi-enabled');
            if (result?.data?.length >= self.options.numPaginationToShow) {
                $spotlightCarousel
                    .find('.swiper-pagination-wrapper')
                    .addClass('swiper-pagination-wrapper--offset');
            }

            // add contentIds attr for tracking
            let contentIds = [];
            result?.data?.forEach((result) => {
                const contentId = result?.showMovieId ?? '';
                contentIds.push(contentId !== '0' ? contentId : '');
            });
            $spotlightCarousel
                .find('.swiper-pagination')
                .attr('data-contentIds', contentIds.join('|'));
        }

        if (this.virtualSlides) {
            swiperParameters.virtual = {
                slides: [],
            };
        }

        if (this.options.centerInsufficientSlides) {
            swiperParameters.centerInsufficientSlides = true;
        }

        // START A/B TEST CBSCOMSUB-8936: Consolidated Content Bucket
        if (this.options.customConsolidatedBucket) {
            swiperParameters.resizeObserver = true;
        }
        // END A/B TEST CBSCOMSUB-8936: Consolidated Content Bucket

        this.swiper = new Swiper(this._.querySelector('.wrap'), swiperParameters);

        this.__appendVirtualSlides(true);
        if (CBS.Registry.lazyImage) {
            setTimeout(CBS.Registry.lazyImage.revalidate, 5);
        }

        // START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel
        if (allowContinuousScroll(this._)) {
            try {
                (async () => {
                    const ContinuousScroll = (
                        await import('../../abTests/testClasses/HomePageContinuousScroll')
                    ).default;
                    this.continuousEndScroll = new ContinuousScroll(this.swiper);
                })();
            } catch (e) {
                console.error('Continuous endless scroll failed to load', e);
            }
        }
        // END AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel

        this.__setupSwiperInternalEvents();
    }

    /**
     * proxy listeners for internal element of slick
     * @param slick
     * @private
     */
    __setupSwiperInternalEvents() {
        const self = this;

        if (!self.position) {
            self.position = self._wrapper.parents('.js-le-carousel').data('position');
        }

        this.swiper.on('slideNextTransitionStart', () => {
            self.__revalidateLazyLoadImages();
            this.__unhideMetadata();
            // adding this to uncheck the checkbox when the slide is changed if the edit is not on still needs some work for slides that are not in view
            if (!$(this).closest('.video-carousel-box').hasClass('editOn')) {
                $(this).parent().find('input:checkbox[name=editor]').attr('checked', false);
            }
        });

        this.swiper.on('slideNextTransitionEnd', function () {
            // START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel
            self.continuousEndScrollNextBuffer = false;
            // END AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel

            if (self.resizing) return;
            if (
                this.activeIndex + this.params.slidesPerView >=
                self.lastIndex - this.params.slidesPerView
            ) {
                self.__nextBuffer();
            }

            // START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel
            if (self.continuousEndScroll) {
                self.continuousEndScroll.setInitialCarouselCycle();
            }
            if (self.continuousEndScroll && self.continuousEndScrollNextBuffer === false) {
                self.continuousEndScroll.appendInfiniteSlides();
            }
            // START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel

            if (self?.options?.xhrResult?.data[0]?.addToMyListContentId) {
                initializedAddToMyListFromYAMLInstance()
                    .setCarouselTrackingData(self._wrapper.get(0))
                    .setClickActionsOnWatchCTAWrappers(self.swiper.visibleSlides);
            }

            // START A/B TEST PLGRWEB-1649-add to my list button from yaml
            //addToMyListContentId will only be set in variant
            if (
                self?.options?.xhrResult?.data[0]?.addToMyListContentId &&
                window.abTests.ShowMoviePageAddToMyListButton
            ) {
                window.abTests.ShowMoviePageAddToMyListButton.setClickActionsOnWatchCTAWrappers(
                    self.swiper.visibleSlides,
                );
            }
            // END A/B TEST PLGRWEB-1649-add to my list button from yaml

            self.__trackCarousels();

            if (self.fathomTracking) {
                self.fathomTracking.updateFathomTracking(self.position);
            }
            if (self.impressionTracking) {
                self.impressionTracking.updateImpressionTracking(self.position);
            }

            //make sure this change function is available to all slides as currently it's only available to the first set.
            $('input[name=editor]').change(function () {
                if ($(this).is(':checked')) {
                    $(this).parent().children('.overlay').css('opacity', '.55');
                    $(this).parent().children('.overlay').css('background-color', '#1D1D1D');
                } else {
                    $(this).parent().children('.overlay').css('opacity', '1');
                    $(this)
                        .parent()
                        .children('.overlay')
                        .css(
                            'background',
                            'linear-gradient(180deg, rgba(0,0,0,0.5508578431372548) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 100%)',
                        );
                }
            });
        });

        if (this.hasFathomTracking) {
            this.swiper.on('slidePrevTransitionEnd', function () {
                if (self.resizing) return;
                if (self.fathomTracking) {
                    self.fathomTracking.updateFathomTracking(self.position);
                }
                if (self.impressionTracking) {
                    self.impressionTracking.updateImpressionTracking(self.position);
                }
            });
        }

        let moveTimeout;
        this.swiper.on('sliderMove', () => {
            if (moveTimeout) {
                clearTimeout(moveTimeout);
            }

            moveTimeout = this.__revalidateLazyLoadImages(20);
        });

        let resizeTimeout;
        this.swiper.on('resize', function () {
            if ($(window).width() !== self.prevWidth) {
                self.resizing = CarouselTracking.resizing = Tracking.resizing = true;
                self.prevWidth = $(window).width();
                let breakpoints = self.__checkBreakpoint();

                if (
                    this.slides.length < self.maxSlides &&
                    this.activeIndex > 0 &&
                    self.lastBreakPoint.slidesPerView > breakpoints.slidesPerView
                ) {
                    this.slideTo(0);
                }

                self.lastBreakPoint = breakpoints;
                self.virtualSlides ? this.virtual.update() : this.slideTo(this.activeIndex);
                self.__setArrowPosition();

                if (resizeTimeout) {
                    clearTimeout(resizeTimeout);
                }

                if (self.hasFathomTracking) {
                    resizeTimeout = setTimeout(() => {
                        if (self.fathomTracking) {
                            if (self.swiper.params.slidesPerGroup !== self.lastSlidesPerGroup) {
                                self.fathomTracking.updateFathomResizePerCarousel(self.position);
                                if (self.impressionTracking) {
                                    self.impressionTracking.updateImpressionResizePerCarousel(
                                        self.position,
                                    );
                                }
                                self.lastSlidesPerGroup = self.swiper.params.slidesPerGroup;
                            }
                            if (JsCarousel.lastCarouselFlag === self._wrapper.data('displayid')) {
                                self.fathomTracking.updateFathomResize();
                                if (self.impressionTracking) {
                                    self.impressionTracking.updateImpressionResize();
                                }
                            }
                        }
                        self.resizing = CarouselTracking.resizing = Tracking.resizing = false;
                    }, 1000);
                }
            }
        });

        // START A/B TEST PLGRWEB-1558 Live Sports Data Carousel
        if (window?.abTests?.LiveSportsDataCarousel?.hasVariants()) {
            this.swiper.on('slideChangeTransitionEnd', function () {
                window?.abTests?.LiveSportsDataCarousel?.initEvents();
            });
        }
        // END A/B TEST PLGRWEB-1558 Live Sports Data Carousel
    }

    __trackCarousels() {
        new JsCarouselTracking(this._, this);
    }

    _redoCounter = 0;

    initFocusable() {
        if (!this.swiper) {
            if (++this._redoCounter < 10) {
                setTimeout(() => {
                    this.initFocusable();
                }, 100);
            }
        } else {
            UpdateFocusableCarousel(this.swiper);
        }
    }

    /**
     * All event listeners in general use should be here
     * @private
     */
    __eventListeners() {
        const self = this;

        this._wrapper.on('templateDidRender', () => {
            // wait for last progress bar with an animation to complete, then force em not to reRender on resize

            this.initFocusable();
            self._wrapper.closest('.carousel').removeClass('hidden');
            let sel = $(this._swiperContainer)
                .find('.progress-bar')
                .not('[percentage=0]')
                .last()
                .find('.bar');
            sel.on('webkitAnimationEnd', () => {
                this.__appendHasRenderedClass();
            });
            sel.on('animationend', () => {
                this.__appendHasRenderedClass();
            });
        });

        // Hide carousel if data fails to load or display warning if it fails with a 500 on lower environments
        this._wrapper.on('videoDataLoadError', (e, httpStatusCode = null) => {
            if (CBS.Registry.debuggable && httpStatusCode === 500) {
                self._wrapper.replaceWith(
                    `<div class="carousel-failure-warning">Failed to load carousel: status code ${httpStatusCode}</div>`,
                );
                console.warn(
                    `Failed to load carousel: status code: ${httpStatusCode}`,
                    this.options,
                );
                return;
            }
            if (this.options.isMyListCarousel) {
                self._wrapper.closest('.js-le-carousel').hide();
            } else {
                self._wrapper.closest('.js-le-carousel').remove();
            }
        });
    }

    __appendHasRenderedClass() {
        this._swiperContainer.find('.bar').each((idx, el) => {
            $(el).addClass('hasRendered');
        });
    }

    __unhideMetadata() {
        // unhide carousel metadata CBSSITE-12262
        $('.info-wrapper:has(.info-onnow-wrapper)').css('display', 'block');
        $('.info-wrapper:has(.title-wrapper)').css('display', 'block');
    }

    __nextBuffer() {
        if (!this.buffSize || this.options.useBuffer === false) return;
        if (this.currentParams.offset + this.buffSize >= this.total) return;
        const self = this;
        let breakpoint = this.__checkBreakpoint();

        // START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel
        this.continuousEndScrollNextBuffer = true;
        // START AB Test feature/PLGRWEB-1306-Homepage-endless-scroll-carousel

        let buffSize = 20;
        let endpoint = this.__buildEndpoint({
            offset: self.currentParams.offset + self.buffSize,
            limit: buffSize,
        });
        this.buffSize = buffSize;

        this.__pullData(endpoint, (result) => {
            self.virtualSlides = this.options
                .template({
                    ...result,
                    getLocale: getLocale,
                    zoomCarouselEffectClass: this.zoomCarouselEffectClass,
                    displayShowTitle: result?.displayShowTitle ?? false,
                })
                .split('<div class="break"></div>')
                .slice(0, -1);
            self.__appendVirtualSlides(false);
            self.__revalidateLazyLoadImages();
        });
    }

    __appendVirtualSlides(force) {
        if (this.virtualSlides && this.virtualSlides.length) {
            this.swiper.virtual.appendSlide(this.virtualSlides);
            this.virtualSlides = null;
        }
    }

    __renderLayout(result, callback) {
        const self = this;
        let profileDropdown =
            result?.includeProfileDropdown === true &&
            result?.carouselPresentationStyle === 'default'
                ? new CarouselProfileDropdown()
                : undefined;

        // START A/B TEST PLGRWEB-1558 Live Sports Data Carousel
        const liveSportsData = result.liveSportsData ?? false;
        // END A/B TEST PLGRWEB-1558 Live Sports Data Carousel

        this._wrapper.html(
            this.options.layout({
                ...result,
                getLocale: getLocale,
                pvrModel: result.pvr_model ?? '',
                displayShowTitle: result?.displayShowTitle ?? false,
                // START A/B TEST PLGRWEB-1558 Live Sports Data Carousel
                liveSportsDataEnabled: result.liveSportsData ?? false,
                displayScoresToggle: result.displayScoresToggle ?? false,
                // END A/B TEST PLGRWEB-1558 Live Sports Data Carousel
            }),
        );
        this._swiperContainer = this._wrapper.find('.wrap');
        this.virtualSlides = this.options
            .template({
                ...result,
                getLocale: getLocale,
                zoomCarouselEffectClass: this.zoomCarouselEffectClass,
                displayShowTitle: result?.displayShowTitle ?? false,
            })
            .split('<div class="break"></div>')
            .slice(0, -1);

        setTimeout(() => {
            try {
                this.__trackCarousels();
            } catch (e) {}
        }, 100);

        if (result.id === 'brands-carousel') {
            this._wrapper.addClass('brands-carousel');
        }

        // applying shadow fader effect to carousel items
        let orientation = result.orientation;
        if (orientation === 'landscape') {
            this._swiperContainer.find('div.carousel-container').addClass('carousel-fader');
        } else if (orientation === 'square') {
            this._swiperContainer.find('div.carousel-container').addClass('square-cards');
        } else {
            this._swiperContainer.find('div.carousel-container').addClass('portrait-cards');
        }

        setTimeout(() => {
            this.__unhideMetadata(); //Waiting for all wrappers loads
        }, 100);

        this._wrapper.trigger('templateDidRender');
        this._.dispatchEvent(new Event('templateDidRenderV2'));
        callback(result);

        // Initialize Profile Dropdown if carousel has it
        if (!CBS.Registry.platform.isMobile) {
            // Dont add if mobile
            profileDropdown?.addDropDown(
                this.options?.isKeepWatchingCarousel,
                this.options?.isMyListCarousel,
                this._wrapper.get(0).getAttribute('id'),
            );
        }

        // START A/B TEST PLGRWEB-1558 Live Sports Data Carousel
        if (liveSportsData) {
            let carouselPos = this._wrapper.parents('.js-le-carousel').data('position') ?? null;
            window?.abTests?.LiveSportsDataCarousel?.init(this.options, carouselPos);
        }
        // END A/B TEST PLGRWEB-1558 Live Sports Data Carousel
    }

    /**
     * perform pulling data & render layout
     * @param endpoint
     * @param callback This callback will be fired data successfully pulled and rendered
     */
    __pullAndRender(endpoint, callback) {
        const self = this;

        // if no endpoint, then setting up for static carousel (no buffering)
        if (endpoint === false) {
            this.__setupSettingsForPhpCarousel();

            // START A/B TEST PLGRWEB-1649-add to my list button from my list page
            if (
                CBS?.AbTestsConfigs?.modules?.MyListPageAddToMyList?.inVariants &&
                this._wrapper.attr('id') === 'rfy-hybrid-carousel'
            ) {
                //has to force virtual mode like JS build carousels because prependSlide and removeSlide only workson virutal slides
                this.virtualSlides = forceVirtualSlides({
                    carouselId: this._wrapper.attr('id'),
                    virtualSlides: this.virtualSlides,
                    carouselWrapper: this._,
                });

                try {
                    (async () => {
                        const addToMyListPage = (
                            await import(`abTests/testClasses/MyListPageAddToMyList`)
                        ).default;
                        new addToMyListPage(this.swiper);
                    })();
                } catch (e) {
                    console.error('addToMyListPage failed to load');
                }

                callback();
                return false;
            }
            // END A/B TEST PLGRWEB-1649-add to my list button from my list page

            this.__trackCarousels();
            this._wrapper.trigger('videoDataLoad');
            callback();
            return false;
        }
        this.__pullData(endpoint, (result) => {
            self.__renderLayout(result, callback);
        });
    }

    /**
     * perform reload of carousel after edit
     */
    __editAndReload(editpoint) {
        const self = this;
        self.currentParams = null;
        self.virtualSlides = null;
        self.lastIndex = 0;
        self.lastBreakPoint = null;
        self.maxSlides = 6;
        self.__init();
    }

    __pullAndRenderCarousel(endpoint, callback) {
        const self = this;

        // if no endpoint, then setting up for static carousel (no buffering)
        if (endpoint === false) {
            this.__setupSettingsForPhpCarousel();
            callback();
            return false;
        }
        this.__pullData(endpoint, (result) => {
            var myObject = {};
            myObject.result = result;
            self.__renderCarousel(myObject);
        });
    }

    __pullData(endpoint, callback) {
        const self = this;

        self.__customMeasureStart(JS_CAROUSEL.LOADED);

        if (self.currentParams && self.currentParams.offset) {
            if (self.currentParams.offset >= self.total) return;
        }

        if (this.options.fetchData) {
            $.post(endpoint, this.options.params, function (resp) {})
                .then((resp) => {
                    try {
                        resp = JSON.parse(resp);
                    } catch (e) {}
                    if (isResponseSuccess(resp)) {
                        let result = resp.result;
                        // save xhr response to options for arrow tracking
                        self.options.xhrResult = result;
                        self.lastIndex += result.data.length;

                        self._wrapper.trigger('videoDataLoad', result);

                        if (result.data.length > 0) {
                            callback(result);
                        }

                        self._wrapper.trigger('videoDataRendered', result);
                    } else {
                        self._wrapper.trigger('videoDataLoadError');
                    }
                    resp.endpoint = endpoint;
                    self.__customMeasureEndSend(JS_CAROUSEL.LOADED, resp);
                })
                .fail((jqXHR) => {
                    self._wrapper.trigger('videoDataLoadError', [jqXHR.status]);
                });
        } else {
            $.getJSON(endpoint, function (resp) {
                if (isResponseSuccess(resp)) {
                    let result = resp.result;
                    // save xhr response to options for arrow tracking
                    self.options.xhrResult = result;
                    self.lastIndex += result.data.length;

                    self._wrapper.trigger('videoDataLoad', result);

                    if (result.data.length > 0) {
                        callback(result);
                    }
                    self._wrapper.trigger('videoDataRendered', result);
                } else {
                    Tracking.removeNumCarousels(1);
                    CarouselTracking.removeNumCarousels(1);
                    self._wrapper.trigger('videoDataLoadError');
                }
            })
                .always((resp) => {
                    try {
                        resp = JSON.parse(resp);
                    } catch (e) {}
                    resp.endpoint = endpoint;
                    self.__customMeasureEndSend(JS_CAROUSEL.LOADED, resp);
                })
                .fail((jqXHR) => {
                    self._wrapper.trigger('videoDataLoadError', [jqXHR.status]);
                });
        }
    }

    __customMeasureStart(actionName) {
        customMeasure.start(actionName);
    }

    __customMeasureEndSend(actionName, resp) {
        let self = this;
        let carouselIndex = parseInt($('.js-le-carousel .carousel').index(self._wrapper));
        let position = carouselIndex > -1 ? carouselIndex + 1 : null;

        let customMeasureData = {
            endpoint: resp.endpoint ?? '',
            success: resp?.result?.data?.length > 0 ? true : false,
            position: position,
        };

        if (typeof resp.result !== 'undefined') {
            customMeasureData.id = resp.result.id ?? '';
            customMeasureData.title = resp.result.title ?? '';
            customMeasureData.carouselPresentationStyle =
                resp?.result?.carouselPresentationStyle ?? '';
        }

        customMeasure.end(actionName);

        customMeasure.sendEvent({
            eventName: actionName,
            customData: customMeasureData,
        });
    }

    /**
     * Applying params values into the endpoint
     *
     * @param merge These values that will be merged with default params
     */
    __buildEndpoint(merge = {}) {
        let self = this;
        if (
            typeof this.options.endPoint === 'undefined' ||
            this.options.endPoint === null ||
            this.options.endPoint.length <= 0
        ) {
            return false;
        }

        let breakpoint = this.__checkBreakpoint();
        this.buffSize = 20;

        let defaultParams = {
            offset: 0,
            limit: this.buffSize,
            excludeShow: 0,
            season: 1,
        };

        let params = $.extend(defaultParams, this.currentParams, merge);

        if (this.currentParams === undefined) {
            params = $.extend(defaultParams, this.options.params, merge);
        }

        // carryover current params
        this.currentParams = params;

        let endpoint = this.options.endPoint;
        for (let property in params) {
            if (params.hasOwnProperty(property)) {
                endpoint = endpoint.replace('{' + property + '}', params[property]);
            }
        }

        if (
            !this.options.editPoint ||
            !Array.isArray(this.excludedContentIds) ||
            !this.excludedContentIds.length
        ) {
            return endpoint;
        } else {
            const endTimeKey = 'endTimeForStoredKeepWatchingIDs';
            const newEndTime = +new Date() + 180000;

            if (localStorage.getItem(endTimeKey)) {
                if (this.addedNewContentId) {
                    localStorage.removeItem(endTimeKey);
                    localStorage.setItem(endTimeKey, newEndTime);
                }
            } else {
                localStorage.setItem(endTimeKey, newEndTime);
            }

            this.deletedStoredTimer = setInterval(() => {
                if (localStorage.getItem(endTimeKey) - new Date() <= 0) {
                    self.clearContentIdsStored();
                }
            }, 100);
            //pass excluded contentIds to endpoint so they are not included in the carousel
            return endpoint + 'exclude/' + this.excludedContentIds + '/';
        }
    }

    /**
     * Renders carousel when data is available.
     *  @param data
     *  @private
     */
    __renderCarousel(data) {
        const self = this;
        this.options.useBuffer = false;

        let result = data.result;

        this._wrapper.html(
            this.options.layout({
                title: result.title,
                todaysDate: result.todaysDate,
                editable: result.editable,
                getLocale: getLocale,
                redDot: result.redDot,
            }),
        );

        this._swiperContainer = this._wrapper.find('.wrap');

        this.virtualSlides = this.options
            .template({
                ...result,
                getLocale: getLocale,
                zoomCarouselEffectClass: this.zoomCarouselEffectClass,
                displayShowTitle: result?.displayShowTitle ?? false,
            })
            .split('<div class="break"></div>')
            .slice(0, -1);

        if (result.editable) {
            this._wrapper.find('.carousel-container').addClass('carousel-fader');
        }

        // applying shadow fader effect to carousel items
        let orientation = result.orientation;
        if (orientation === 'landscape') {
            this._swiperContainer.find('div.carousel-container').addClass('carousel-fader');
        } else if (orientation === 'portrait') {
            this._swiperContainer.find('div.carousel-container').addClass('portrait-cards');
        } else if (orientation === 'square') {
            this._swiperContainer.find('div.carousel-container').addClass('square-cards');
        }

        this._wrapper.trigger('templateDidRender');
        this._wrapper.trigger('videoDataRendered');

        this.__initSwiper();
        this.total = (result && result.total) || 0;
        this.__trackingArrow();
        this.__trackCarousels();

        this.__initEdit();

        this._wrapper.find('.carousel-container').css('opacity', '1');
    }

    /**
     * Getting the current breakpoint
     *
     * @returns currentBreakpoint
     * @private
     */
    __checkBreakpoint() {
        let windowWidth = window.innerWidth || $(window).width();
        let breakpoints = this.options.responsive;
        let currentBreakpoint;
        let previousBreakpoint = 0;

        for (let breakpoint in breakpoints) {
            if (windowWidth < breakpoint) {
                currentBreakpoint = breakpoints[previousBreakpoint];
                break;
            }
            previousBreakpoint = breakpoint;
        }

        if (!currentBreakpoint) {
            currentBreakpoint = breakpoints[previousBreakpoint];
        }

        return currentBreakpoint;
    }

    __revalidateLazyLoadImages(timeout) {
        return setTimeout(() => {
            if (CBS.Registry.lazyImage) {
                for (const visibleSlide of this.swiper.visibleSlides) {
                    CBS.Registry.lazyImage.load(visibleSlide.querySelectorAll('img'));
                }
                let nextIndex =
                    this.swiper.visibleSlidesIndexes[this.swiper.visibleSlidesIndexes.length - 1] +
                    1;
                if (this.swiper.slides[nextIndex]) {
                    CBS.Registry.lazyImage.load(this.swiper.slides[nextIndex].querySelector('img'));
                    CBS.Registry.lazyImage.revalidate();
                }
            }
        }, timeout || 5);
    }
    getWrapper() {
        return this._wrapper;
    }
}

/**
 * This is the proxy construct the JsCarousel
 */
$.fn.JsCarousel = function () {
    let _ = this;
    let opt = arguments[0];

    for (let i = 0; i < _.length; i++) {
        new JsCarousel(_[i], opt);
    }
};

/*
 * HELPER FUNCTIONS
 */
function isResponseSuccess(resp) {
    return (
        typeof resp == 'object' &&
        resp.success &&
        typeof resp.result != 'undefined' &&
        typeof resp.result.data != 'undefined' &&
        resp.result.data.length > 0
    );
}

function toTitleCase(str) {
    return str.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1);
    });
}
